import i18n from 'i18next';

import { PaymentTypes } from '@cashier/cashier-model';

import { compareDecimals } from './compareDecimals';

interface ValidationReport {
  isValid: boolean;
  errorMessage?: string;
}

export const AmountFieldValidator = {
  byBalance: (value: string, balance: string): ValidationReport => {
    const isValid = compareDecimals(value, balance) <= 0;

    if (!isValid) {
      return {
        isValid: false,
        errorMessage: i18n.t('insufficientFunds.message'),
      };
    }

    return { isValid: true };
  },
  byCharacterType: (value: string): ValidationReport => {
    const isValid = /^\d+(,\d{1,})?(\.\d{1,})?$/.test(value);

    if (!isValid) {
      return {
        isValid: false,
        errorMessage: i18n.t('amountCharactersInvalid.message'),
      };
    }

    return { isValid: true };
  },
  byLimit: (
    value: string,
    limit: {
      min?: number;
      max?: number;
    },
    metaData: {
      paymentType: PaymentTypes;
    },
  ): ValidationReport => {
    const { min, max } = limit;

    const isValid =
      !!value &&
      compareDecimals(value, String(min)) >= 0 &&
      compareDecimals(value, String(max)) <= 0;

    if (!isValid) {
      const limitsMessageMapper = {
        [PaymentTypes.Deposit]: i18n.t(
          'minMaxValidationFailed.deposit.message',
        ),
        [PaymentTypes.Withdrawal]: i18n.t(
          'minMaxValidationFailed.withdrawal.message',
        ),
      };

      return {
        isValid: false,
        errorMessage: limitsMessageMapper[metaData.paymentType],
      };
    }

    return { isValid: true };
  },
  byScale: (value: string, scale: number): ValidationReport => {
    const absoluteScale = Math.abs(scale);

    if (
      Math.sign(scale) === -1 &&
      Number(value) % Math.pow(10, absoluteScale) !== 0
    ) {
      return {
        isValid: false,
        errorMessage: i18n.t(
          'amountMultipleOfTensValidationFailed.message',
          'amountMultipleOfTensValidationFailed.message',
          { multipleTo: Math.pow(10, absoluteScale) },
        ),
      };
    }

    if (Math.sign(scale) === 1) {
      const amountParts = String(value).split('.');
      if (amountParts.length !== 1 && amountParts[1].length > absoluteScale) {
        return {
          isValid: false,
          errorMessage: i18n.t(
            'amountDecimalValidationFailed.message',
            'amountDecimalValidationFailed.message',
            { decimalCount: absoluteScale },
          ),
        };
      }
    }

    if (Math.sign(scale) === 0 && Math.floor(Number(value)) !== Number(value)) {
      return {
        isValid: false,
        errorMessage: i18n.t('amountWholeNumberValidationFailed.message'),
      };
    }

    return { isValid: true };
  },
};
